class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.setupAutoClose(); // live-match
  }

  /* live-match: after an add-to-cart the drawer closes itself after 3 s (Robin's request,
     live custom.js closed it after 6 s). The timer pauses while the pointer is over the
     drawer and is cancelled for good as soon as the user interacts with it. */
  setupAutoClose() {
    this.autoCloseDelay = 3000;
    this.autoCloseTimer = null;
    this.autoCloseRemaining = 0;
    this.autoCloseStartedAt = 0;
    this.autoCloseArmed = false;
    // <cart-drawer> spans the whole viewport (overlay included), so hover is tracked on the
    // visible panel (.drawer__inner, re-rendered on every add -> delegated listeners).
    this.addEventListener('mouseover', (e) => {
      if (e.target.closest('.drawer__inner')) this.pauseAutoClose();
      else this.resumeAutoClose();
    });
    this.addEventListener('mouseleave', () => this.resumeAutoClose());
    // Any real interaction with the panel cancels the auto-close. (No 'focusin': open()
    // moves focus into the drawer programmatically via trapFocus.)
    ['pointerdown', 'keydown', 'touchstart', 'wheel'].forEach((type) =>
      this.addEventListener(type, (e) => {
        if (e.target.closest && e.target.closest('.drawer__inner')) this.cancelAutoClose();
      }, { passive: true })
    );
  }

  startAutoClose() {
    this.cancelAutoClose();
    this.autoCloseArmed = true;
    this.autoCloseRemaining = this.autoCloseDelay;
    const panel = this.querySelector('.drawer__inner');
    if (panel && panel.matches(':hover')) return; // starts when the pointer leaves the panel
    this.runAutoClose();
  }

  runAutoClose() {
    if (!this.autoCloseArmed) return;
    this.autoCloseStartedAt = Date.now();
    this.autoCloseTimer = setTimeout(() => {
      this.autoCloseArmed = false;
      this.autoCloseTimer = null;
      if (this.classList.contains('active')) this.close();
    }, this.autoCloseRemaining);
  }

  pauseAutoClose() {
    if (!this.autoCloseArmed || !this.autoCloseTimer) return;
    clearTimeout(this.autoCloseTimer);
    this.autoCloseTimer = null;
    this.autoCloseRemaining = Math.max(0, this.autoCloseRemaining - (Date.now() - this.autoCloseStartedAt));
  }

  resumeAutoClose() {
    if (this.autoCloseArmed && !this.autoCloseTimer) this.runAutoClose();
  }

  cancelAutoClose() {
    this.autoCloseArmed = false;
    if (this.autoCloseTimer) clearTimeout(this.autoCloseTimer);
    this.autoCloseTimer = null;
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.cancelAutoClose(); // live-match
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
      this.startAutoClose(); // live-match: renderContents() is only called after an add-to-cart
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
